/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { EventType, IEvent } from '../@types/event';
import type { XHubChatEvent } from '../models/event/event';
import type { Room } from '../models/room/room';
import type { RoomMember } from '../models/room/room-member';
import type { RoomState } from '../models/room/room-state';
import type { RoomSummary } from '../models/room/room-summary';
import type { User } from '../models/user/user';
import type { RoomData } from '../sync/interface';
import type { IStore, IStoredClientOpts, UserCreator } from './interfaces/store';

import { KnownMembership } from '../@types/membership';
import { RoomStateEvent } from '../@types/room-state';
import { EventTimeline } from '../models/event/event-timeline';

/**
 * Random access memory store. Data is lost when the process exits.
 * Store all data in this session: users, rooms, events etc.
 */

export type IOpts = {
  /** The local storage instance to persist some forms of data such as tokens. Rooms will NOT be stored. */
  localStorage?: Storage;
};

export class MemoryStore implements IStore {
  /**
   * This is used for logging and debugging. To know which store instance is logging.
   */
  private readonly _NAME = 'MemoryStore';

  private rooms: Record<string, Room> = {}; // roomId: Room
  private users: Record<string, User> = {}; // userId: User
  private syncToken: string | null = null;

  public accountData: Map<string, XHubChatEvent> = new Map(); // type: content

  private pendingEvents: { [roomId: string]: Partial<IEvent>[] } = {};

  protected createUser?: UserCreator;
  private clientOptions?: IStoredClientOpts;

  /**
   * The local storage instance to persist some forms of data such as tokens. Rooms will NOT be stored.
   */
  protected readonly localStorage?: Storage;

  /**
   * Construct a new in-memory data store for the Matrix Client.
   * @param opts - Config options
   */
  public constructor(opts: IOpts = {}) {
    this.localStorage = opts.localStorage;
  }

  /** @returns whether or not the database was newly created in this session. */
  public isNewlyCreated(): Promise<boolean> {
    return Promise.resolve(true);
  }

  /**
   * ======================================================================
   *                                  SYNC TOKEN
   * ======================================================================
   */
  /**
   * Retrieve the token to stream from.
   * @returns The token or null.
   */
  public getSyncToken(): string | null {
    return this.syncToken;
  }

  /**
   * Set the token to stream from.
   * @param token - The token to stream from.
   */
  public setSyncToken(token: string): void {
    this.syncToken = token;
  }
  /**
   * ======================================================================
   *                               END SYNC TOKEN
   * ======================================================================
   */

  /**
   * ======================================================================
   *                                ROOM
   * ======================================================================
   */

  /**
   * setRoomsData does nothing as there is no backing data store.
   *
   * @param roomsData - The sync data
   * @returns An immediately resolved promise.
   */
  public setRoomsData(roomsData: Record<string, RoomData>): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Retrieve all rooms saved in the store.
   * @returns A list of rooms, or null if no rooms are saved.
   */
  public getSavedRooms(): Promise<RoomData[] | null> {
    return Promise.resolve(null);
  }

  /**
   * Store the given room.
   * @param room - The room to be stored. All properties must be stored.
   */
  public storeRoom(room: Room): void {
    this.rooms[room.roomId] = room;

    const currentRoomState = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
    if (currentRoomState) {
      // add listeners for room member changes so we can keep the room member map up-to-date.
      currentRoomState.on(RoomStateEvent.Members, this.onRoomMember);
      // add existing members
      currentRoomState.getMembers().forEach((m) => {
        this.onRoomMember(null, currentRoomState, m);
      });
    }
  }

  /**
   * Called when a room member in a room being tracked by this store has been
   * updated.
   */
  private onRoomMember = (event: XHubChatEvent | null, state: RoomState, member: RoomMember): void => {
    if (member.membership === KnownMembership.Invite) {
      // We do NOT add invited members because people love to typo user IDs
      // which would then show up in these lists (!)
      return;
    }

    const user = this.users[member.userId] || this.createUser?.(member.userId, member.channel_slug || member.userId);
    if (member.name) {
      user.setDisplayName(member.name);
      if (member.events.member) {
        user.setRawDisplayName(member.events.member.getDirectionalContent().displayname);
      }
    }
    if (member.events.member && member.events.member.getContent().avatar_url) {
      user.setAvatarUrl(member.events.member.getContent().avatar_url);
    }
    this.users[user.userId] = user;
  };

  /**
   * Retrieve a room by its' room ID.
   * @param roomId - The room ID.
   * @returns The room or null.
   */
  public getRoom(roomId: string): Room | null {
    const rs = this.rooms[roomId] || null;
    return rs;
  }

  /**
   * Retrieve all known rooms.
   * @returns A list of rooms, which may be empty.
   */
  public getRooms(): Room[] {
    return Object.values(this.rooms);
  }

  /**
   * Permanently delete a room.
   */
  public removeRoom(roomId: string): void {
    if (this.rooms[roomId]) {
      this.rooms[roomId].getLiveTimeline().getState(EventTimeline.FORWARDS)?.removeListener(RoomStateEvent.Members, this.onRoomMember);
    }
    delete this.rooms[roomId];
  }

  /**
   * Retrieve a summary of all the rooms.
   * @returns A summary of each room.
   */
  public getRoomSummaries(): RoomSummary[] {
    return Object.values(this.rooms).map(room => room.summary!);
  }

  /**
   * ======================================================================
   *                            END ROOM
   * ======================================================================
   */

  /**
   * ======================================================================
   *                              USER
   * ======================================================================
   */

  /**
   * Store a User.
   * @param user - The user to store.
   */
  public storeUser(user: User): void {
    this.users[user.userId] = user;
  }

  /**
   * Retrieve a User by its' user ID.
   * @param userId - The user ID.
   * @returns The user or null.
   */
  public getUser(userId: string): User | null {
    return this.users[userId] || null;
  }

  /**
   * Retrieve all known users.
   * @returns A list of users, which may be empty.
   */
  public getUsers(): User[] {
    return Object.values(this.users);
  }

  public setUserCreator(creator: UserCreator): void {
    this.createUser = creator;
  }
  /**
   * ======================================================================
   *                             END USER
   * ======================================================================
   */

  /**
   * Retrieve scrollback for this room.
   * @param room - The matrix room
   * @param limit - The max number of old events to retrieve.
   * @returns An array of objects which will be at most 'limit'
   * length and at least 0. The objects are the raw event JSON.
   */
  public scrollback(room: Room, limit: number): XHubChatEvent[] {
    return [];
  }

  /**
   * ======================================================================
   *                             EVENTS
   * ======================================================================
   */

  public async getPendingEvents(roomId: string): Promise<Partial<IEvent>[]> {
    return this.pendingEvents[roomId] ?? [];
  }

  public async setPendingEvents(roomId: string, events: Partial<IEvent>[]): Promise<void> {
    this.pendingEvents[roomId] = events;
  }

  /**
   * Store events for a room. The events have already been added to the timeline
   * @param room - The room to store events for.
   * @param events - The events to store.
   * @param token - The token associated with these events.
   * @param toStart - True if these are paginated results.
   */
  public storeEvents(room: Room, events: XHubChatEvent[], token: string | null, toStart: boolean): void {
    // no-op because they've already been added to the room instance.
  }
  /**
   * ======================================================================
   *                            END EVENTS
   * ======================================================================
   */

  /**
   * Store user-scoped account data events.
   * N.B. that account data only allows a single event per type, so multiple
   * events with the same type will replace each other.
   * @param events - The events to store.
   */
  public storeAccountDataEvents(events: XHubChatEvent[]): void {
    events.forEach((event) => {
      // MSC3391: an event with content of {} should be interpreted as deleted
      const isDeleted = !Object.keys(event.getContent()).length;
      if (isDeleted) {
        this.accountData.delete(event.getType());
      } else {
        this.accountData.set(event.getType(), event);
      }
    });
  }

  /**
   * Get account data event by event type
   * @param eventType - The event type being queried
   * @returns the user account_data event of given type, if any
   */
  public getAccountData(eventType: EventType | string): XHubChatEvent | undefined {
    return this.accountData.get(eventType);
  }

  /**
   * @returns If there is a saved sync, the nextBatch token
   * for this sync, otherwise null.
   */
  public getSavedSyncToken(): Promise<string | null> {
    return Promise.resolve(null);
  }

  public getClientOptions(): Promise<IStoredClientOpts | undefined> {
    return Promise.resolve(this.clientOptions);
  }

  public storeClientOptions(options: IStoredClientOpts): Promise<void> {
    this.clientOptions = Object.assign({}, options);
    return Promise.resolve();
  }

  /**
   * We never want to save because we have nothing to save to.
   *
   * @returns If the store wants to save
   */
  public wantsSave(): boolean {
    return false;
  }

  /**
   * Save does nothing as there is no backing data store.
   * @param force - True to force a save (but the memory
   *     store still can't save anything)
   */
  public save(force?: boolean): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Startup does nothing as this store doesn't require starting up.
   * @returns An immediately resolved promise.
   */
  public startup(): Promise<void> {
    return Promise.resolve();
  }

  /**
   * Delete all data from this store.
   * @returns An immediately resolved promise.
   */
  public deleteAllData(): Promise<void> {
    this.rooms = {
      // roomId: Room
    };
    this.users = {
      // userId: User
    };
    this.syncToken = null;
    this.accountData = new Map(); // type : content
    return Promise.resolve();
  }

  public async destroy(): Promise<void> {
    // Nothing to do
  }
}
